import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { Editor } from '@tiptap/core';

import SimpleModal from 'lib/modal/simple-modal';
import { SimpleModalContentStyled } from 'lib/modal/simple-modal.styles';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { NegativeButton, PrimaryButton } from 'approot/shared/styles/buttons';
import {
  LabelStyled,
  TextInputStyled,
} from 'approot/shared/styles/inputs.style';
import * as Styled from './richtext-link-modal.style';
import { setLink, getLink, removeLink } from './richtext.utils';
import { PStyled } from 'approot/shared/styles/typography.style';
import { normalizeHttpUrl } from 'approot/shared/logic/normalize-http-url';
import { Box } from 'approot/shared/design-system/box/box.style';

export type Props = {
  editor: Editor;
  closeModal: () => void;
};

export const LINK_MODAL_ID = 'link-modal';
const LINK_URL_FIELD = 'link-url';

const RichtextLinkModal = ({ editor, closeModal }: Props) => {
  const link = useMemo(() => getLink(editor), [editor]);
  const [isEditingLink, setIsEditingLink] = useState<boolean>(!link);
  const [updatedLink, setUpdatedLink] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleCloseModal = useCallback(() => {
    closeModal();
    editor.chain().focus();
  }, [closeModal, editor]);

  const onSubmit = useCallback(() => {
    setLink(editor, updatedLink);
    handleCloseModal();
  }, [handleCloseModal, editor, updatedLink]);

  const debounceLinkChange = debounce(
    event => setUpdatedLink(normalizeHttpUrl(event.target.value)),
    150
  );

  const title = link ? 'Edit Link' : 'Add Link';

  useEffect(() => {
    // focus the link field when the modal contents change
    if (isEditingLink && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingLink]);

  return (
    <SimpleModal
      isVisible
      title={title}
      headerTitle={title}
      onClose={handleCloseModal}
      size={isEditingLink ? 456 : 288}
      openBodyClassName={LINK_MODAL_ID}
    >
      <SimpleModalContentStyled>
        <Styled.RichtextLinkModalView $hide={!isEditingLink}>
          <form onSubmit={onSubmit}>
            <Box $flexDirection="column" $marginBottom={24}>
              <LabelStyled htmlFor={LINK_URL_FIELD}>Link</LabelStyled>
              <TextInputStyled
                ref={inputRef}
                id={LINK_URL_FIELD}
                defaultValue={link}
                onChange={event => debounceLinkChange(event)}
                placeholder="http://www.link.com/example"
              />
            </Box>

            <Styled.RichtextLinkModalButtons>
              <PrimaryButton type="submit">Save</PrimaryButton>
            </Styled.RichtextLinkModalButtons>
          </form>
        </Styled.RichtextLinkModalView>

        <Styled.RichtextLinkModalView $hide={isEditingLink}>
          <PStyled>{link}</PStyled>

          <Styled.RichtextLinkModalButtons>
            <SecondaryButton
              type="button"
              onClick={() => setIsEditingLink(true)}
            >
              Edit
            </SecondaryButton>
            <NegativeButton
              type="button"
              onClick={() => {
                removeLink(editor);
                handleCloseModal();
              }}
            >
              Remove
            </NegativeButton>
          </Styled.RichtextLinkModalButtons>
        </Styled.RichtextLinkModalView>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default RichtextLinkModal;
